import {
  CSSProperties,
  FC,
  forwardRef,
  PropsWithChildren,
  Ref,
  useContext,
  useMemo,
} from 'react';

import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import styled from '@mint-lib/styled';

import {
  DEFAULT_FIELD_WIDTH,
  MIN_COLUMN_WIDTH,
} from '../../../constants/campaignBuilder.js';
import { TableContentContext } from '../../../context/TableContentContext.jsx';
import { useBuilderTableViewDataSelector } from '../../../hooks/useGetBuilderTableViewData.js';
import usePreparedBuilderTableColumns from '../../../hooks/usePreparedBuilderTableColumns.js';
import useResize from '../../../hooks/useResize.js';
import { useSaveBuilderTableColumnWidth } from '../../../hooks/useSaveBuilderTableView.js';
import { useTableScale } from '../../../hooks/useTableScale.js';
import { TRACKING_EVENTS } from '../../../types/performanceTracking.js';
import BuilderTableHead from './BuilderTable/BuilderTableHead.jsx';

type Props = PropsWithChildren & {
  style: CSSProperties;
};

const InnerElement: FC<Props> = forwardRef(
  ({ children, style }, ref: Ref<HTMLTableRowElement>) => {
    const { tableContent } = useContext(TableContentContext) || {};
    const { columnNames, initialColumns } = usePreparedBuilderTableColumns();
    const scale = useTableScale();
    const { data: builderTableViewId } = useBuilderTableViewDataSelector('id');

    const saveBuilderColumnWidth = useSaveBuilderTableColumnWidth();
    const isReadOnly = !useIsAllowedPermission(
      'campaignPlanningFeature:modify',
    );

    const {
      headerGroups = [],
      getTableProps,
      getTableBodyProps,
      setColumnOrder,
      columnResizing,
      expandedType,
      setExpandedType,
      columns,
      startMeasurePerformance,
    } = tableContent || {};

    const bodyProps = getTableBodyProps?.();

    const columnsWidth = useMemo(() => {
      if (!columns) {
        return 0;
      }

      const width = columns.reduce((acc, item) => {
        const widthItem =
          typeof item.width === 'number' ? item.width * scale : 0;

        return acc + widthItem;
      }, 0);

      // ensure additional width for the sticky ActionsField for convenient editing of the table's last column
      return width + DEFAULT_FIELD_WIDTH * scale;
    }, [columns, scale]);

    const handleSaveColumnWidth = (columnWidth: { [name: string]: number }) => {
      if (!builderTableViewId || isReadOnly) {
        return;
      }

      startMeasurePerformance?.(TRACKING_EVENTS.COLUMN_RESIZE);
      saveBuilderColumnWidth({ id: builderTableViewId, columnWidth });
    };

    const setColumnRefs = useResize({
      columnResizing,
      onSaveColumnWidth: handleSaveColumnWidth,
      minWidth: MIN_COLUMN_WIDTH,
    });

    return (
      <Table $width={columnsWidth} {...getTableProps?.()} style={style}>
        <BuilderTableHead
          headerGroups={headerGroups}
          columnOrder={columnNames}
          setColumnOrder={setColumnOrder || (() => {})}
          columnsFromSchema={initialColumns}
          ref={ref}
          onSetColumnRef={setColumnRefs}
          isResizingColumn={columnResizing?.isResizingColumn}
          expandedType={expandedType}
          setExpandedType={setExpandedType}
        />
        <BuilderTableBody {...bodyProps}>{children}</BuilderTableBody>
      </Table>
    );
  },
);

export default InnerElement;

const BuilderTableBody = styled('div')`
  position: relative;
`;

const Table = styled('div')<{
  $width: number;
}>`
  min-width: 100% !important;
  width: ${({ $width }) => $width}px !important;
  min-height: 100% !important;
`;
